/* eslint-disable css-modules/no-unused-class */
import { FC, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Button, Icon } from 'common-components';
import styles from './editorFooter.module.scss';
import { FooterVariant } from '../customEditorTypes';
import { useTranslationContext } from '../../contexts/translationContext';

const EditorFooter: FC<EditorFooterProps> = (props: EditorFooterProps) => {
    const { secondaryAction, mainAction, styleVariant, percentage = 100, outOfTries, isGameDone } = props;

    const { translate } = useTranslationContext();

    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [animationKey, setAnimationKey] = useState(0);

    const LOADING_MESSAGE_KEYS = [
        'LetsSee',
        'StartingAnalysis',
        'ScanningCode',
        'AIPondering',
        'DeepCheck',
        'ThinkingLonger',
        'ValidatingLogic',
        'CrossReferencing',
        'NearlyThere',
        'FinalSteps',
        'StillWorking',
        'CompletingScan'
    ] as const;

    useEffect(() => {
        if (styleVariant === FooterVariant.LOADING) {
            // Reset to first message and trigger animation immediately
            setLoadingMessageIndex(0);
            setAnimationKey(prev => prev + 1);

            const interval = setInterval(() => {
                setLoadingMessageIndex(prevIndex => {
                    if (prevIndex < 9) {
                        return prevIndex + 1;
                    }
                    return 9 + ((prevIndex - 9 + 1) % 3);
                });
                setAnimationKey(prev => prev + 1);
            }, 3000);

            return () => clearInterval(interval);
        }

        return undefined;
    }, [styleVariant]);

    const getPercentageVariation = () => {
        if (percentage < 25) {
            return {
                title: outOfTries ? translate('IDEResult25OutOfTries') : translate('IDEResult25'),
                bg: <Icon name="errorBoxBG" isPng={true} />,
                cx: 'result-25'
            };
        }
        if (percentage < 50) {
            return {
                title: outOfTries ? translate('IDEResult50OutOfTries') : translate('IDEResult50'),
                bg: null,
                cx: 'result-50'
            };
        }
        if (percentage < 75) {
            return {
                title: outOfTries ? translate('IDEResult75OutOfTries') : translate('IDEResult75'),
                bg: null,
                cx: 'result-75'
            };
        }
        if (percentage < 100)
            return {
                title: outOfTries ? translate('IDEResult100OutOfTries') : translate('IDEResult100'),
                bg: null,
                cx: 'result-100'
            };
        return {
            title: outOfTries ? translate('IDEResult100OutOfTries') : translate('IDEResult100Extended'),
            bg: null,
            cx: 'result-100'
        };
    };

    const variantProps = {
        [FooterVariant.READ_ONLY]: {
            title: isGameDone ? translate('IDEDone') : translate('IDEReadOnly'),
            titleIcon: <Icon name="lockCircled" style={{ marginRight: '1rem' }} />,
            mainAction: translate('Next'),
            secondaryAction: null,
            bg: null,
            cx: 'done'
        },
        [FooterVariant.END_GAME]: {
            title: translate('IDEEndGame'),
            mainAction: translate('EndGame'),
            secondaryAction: null,
            bg: null,
            cx: 'done'
        },
        [FooterVariant.DONE]: {
            title: translate('IDEDone'),
            titleIcon: <Icon name="lockCircled" style={{ marginRight: '1rem' }} />,
            mainAction: translate('Done'),
            secondaryAction: null,
            bg: null,
            cx: 'done'
        },
        [FooterVariant.SUBMIT]: {
            title: null,
            mainAction: translate('Submit'),
            secondaryAction: null,
            bg: null,
            cx: 'submit'
        },
        [FooterVariant.SUBMIT_DISABLED]: {
            title: null,
            mainAction: translate('Submit'),
            secondaryAction: null,
            bg: null,
            cx: 'submit'
        },
        [FooterVariant.LOADING]: {
            title: (
                <span className={styles['loading-text']} key={animationKey}>
                    {translate(LOADING_MESSAGE_KEYS[loadingMessageIndex])}
                </span>
            ),
            mainAction: translate('Submit'),
            secondaryAction: null,
            titleIcon: <Icon name="loader" style={{ marginRight: '0.5rem' }} />,
            bg: null,
            cx: 'submit'
        },
        [FooterVariant.RESULT]: {
            mainAction: outOfTries ? translate('Next') : translate('TryAgain'),
            secondaryAction: outOfTries ? null : translate('SkipTrying'),
            ...getPercentageVariation()
        }
    };

    return (
        <div className={styles.container}>
            <div className={classNames(styles.wrapper, styles[variantProps[styleVariant].cx])}>
                {variantProps[styleVariant].bg && (
                    <div className={styles['bg-wrapper']}>{variantProps[styleVariant].bg}</div>
                )}
                {percentage !== undefined && styleVariant === FooterVariant.RESULT && (
                    <div className={styles.percentage}>{`${percentage}%`}</div>
                )}
                {variantProps[styleVariant].title && (
                    <div className={styles.title}>
                        {(variantProps[styleVariant] as any)?.titleIcon}
                        {typeof variantProps[styleVariant].title === 'string' ? (
                            <span>{variantProps[styleVariant].title}</span>
                        ) : (
                            variantProps[styleVariant].title
                        )}
                    </div>
                )}
                <div className={styles['actions-wrapper']}>
                    {variantProps[styleVariant].secondaryAction && (
                        <div className={styles.action} onClick={secondaryAction}>
                            <span>{variantProps[styleVariant].secondaryAction}</span>
                        </div>
                    )}
                    <Button
                        type={
                            styleVariant === FooterVariant.SUBMIT_DISABLED || styleVariant === FooterVariant.LOADING
                                ? 'gradientNoBorderDisabled'
                                : 'gradientNoBorder'
                        }
                        onClick={mainAction}
                        text={variantProps[styleVariant].mainAction}
                        lowerCase={true}
                    />
                </div>
            </div>
        </div>
    );
};

interface EditorFooterProps {
    secondaryAction: () => void;
    mainAction: () => void;
    styleVariant: FooterVariant;
    percentage?: number;
    outOfTries?: boolean;
    isGameDone?: boolean;
}

export default EditorFooter;
